/**
 * Manual upload.
 *
 * The escape hatch for when the watcher agent is down, or a guest brings a
 * document that never went through the PMS. The legacy system had no such
 * path: if the print integration failed, check-in stopped.
 */

import { useCallback, useRef, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { FileUp, X } from 'lucide-react'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/primitives'
import { useToast } from '@/components/ui/toast'
import { cn, formatBytes, uuid } from '@/lib/utils'

const MAX_MB = 200

export function UploadDialog({
  open,
  deskId,
  onClose,
  onUploaded,
}: {
  open: boolean
  deskId: string
  onClose: () => void
  onUploaded: (jobId: string) => void
}) {
  const toast = useToast()
  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const upload = useMutation({
    mutationFn: async (selected: File) => {
      const body = new FormData()
      body.append('file', selected)
      body.append('desk_id', deskId)
      return api.post<{ job_id: string }>('/capture/upload', body, {
        idempotencyKey: uuid(),
      })
    },
    onSuccess: (result) => {
      toast.success('Reading the document…', 'It will appear in the inbox in a few seconds.')
      setFile(null)
      onUploaded(result.job_id)
    },
    onError: (error: Error) => toast.error('Upload failed', error.message),
  })

  const accept = useCallback(
    (candidate: File | undefined) => {
      if (!candidate) return
      if (candidate.type !== 'application/pdf' && !candidate.name.toLowerCase().endsWith('.pdf')) {
        toast.error('That is not a PDF', 'Only PDF documents can be captured.')
        return
      }
      if (candidate.size > MAX_MB * 1024 * 1024) {
        toast.error('That file is too large', `The limit is ${MAX_MB} MB.`)
        return
      }
      setFile(candidate)
    },
    [toast],
  )

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center scrim px-4"
      role="presentation"
      onClick={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-title"
        className="fade-in w-full max-w-[30rem] rounded-[18px] border border-[var(--border)] bg-[var(--surface)] p-6 shadow-[var(--shadow-lg)]"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 id="upload-title" className="text-[15px] font-semibold">
              Upload a document
            </h2>
            <p className="mt-1 text-[13px] text-[var(--text-muted)]">
              It is read the same way a PMS print is, and lands in your inbox.
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>

        <label
          onDragOver={(event) => {
            event.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault()
            setDragging(false)
            accept(event.dataTransfer.files[0])
          }}
          className={cn(
            'mt-4 flex cursor-pointer flex-col items-center gap-2 rounded-[12px] border-2 border-dashed px-6 py-10 text-center transition-colors',
            dragging
              ? 'border-[var(--brand)] bg-[var(--brand-subtle)]'
              : 'border-[var(--border)] hover:border-[var(--text-muted)]',
          )}
        >
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="sr-only"
            onChange={(event) => accept(event.target.files?.[0])}
          />
          <FileUp className="h-6 w-6 text-[var(--text-muted)]" aria-hidden />
          {file ? (
            <>
              <span className="text-[13px] font-medium">{file.name}</span>
              <span className="text-[12px] text-[var(--text-muted)]">{formatBytes(file.size)}</span>
            </>
          ) : (
            <>
              <span className="text-[13px] font-medium">Drop a PDF here, or click to choose</span>
              <span className="text-[12px] text-[var(--text-muted)]">Up to {MAX_MB} MB</span>
            </>
          )}
        </label>

        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!file}
            loading={upload.isPending}
            onClick={() => file && upload.mutate(file)}
          >
            Upload
          </Button>
        </div>
      </div>
    </div>
  )
}
